import { createContext, type ReactNode, useEffect, useMemo } from 'react';
import { getCookie, setCookie } from 'cookies-next';
import { useGetIso } from '@/api';
import { useUser } from '@/lib/auth/useUser';

/**
 * 사용자의 국가 코드 및 관련 정보를 관리하는 Context
 *
 * @description
 * - 로그인 사용자: user 객체의 countryCode 사용
 * - 비로그인 사용자: 'countryCode' 쿠키 사용
 *
 **/

interface CountryCodeContextValue {
  countryCode: string;
  isLoading: boolean;
  isKR: boolean;
  isReady: boolean;
}
export const CountryCodeContext = createContext<CountryCodeContextValue | undefined>(undefined);
export function CountryCodeProvider({
  children
}: {
  children: ReactNode;
}) {
  const {
    isAuthenticated,
    user,
    isInitialized
  } = useUser();

  // 쿠키에서 국가코드 가져오기
  const countryCodeCookie = getCookie('countryCode');

  // 쿠키 없으면 API 호출
  const {
    data: isoData,
    isLoading: isIsoLoading
  } = useGetIso({
    query: {
      enabled: isInitialized && !countryCodeCookie && !user?.countryCode
    }
  });

  // 위 API로 받아온 국가코드를 쿠키에 저장
  useEffect(() => {
    if (!countryCodeCookie && isoData?.data.isoCode) {
      setCookie('countryCode', isoData.data.isoCode, {
        maxAge: 60 * 60 * 24 * 7
      });
    }
  }, [countryCodeCookie, isoData?.data.isoCode]);

  // 최종 국가코드 결정 (우선순위: 로그인 유저 > 쿠키 > API > 기본값)
  const countryCode = (isAuthenticated && user?.countryCode ? user.countryCode : countryCodeCookie || isoData?.data.isoCode || 'US').toUpperCase();
  const isLoading = !isInitialized || isIsoLoading;
  const value = useMemo(() => ({
    countryCode,
    isKR: countryCode === 'KR',
    isLoading,
    isReady: !isLoading && !!countryCode
  }), [countryCode, isLoading]);
  return <CountryCodeContext.Provider value={value} data-sentry-element="unknown" data-sentry-component="CountryCodeProvider" data-sentry-source-file="CountryCodeProvider.tsx">
      {children}
    </CountryCodeContext.Provider>;
}